<template>
  <div class="card">
    <h2 class="card-title">{{ title || $t('complete_listing_title') }}</h2>
    <div class="desc-ring-row">
      <p class="card-description">
        {{ $t('complete_listing_description') }}
      </p>
      <AdRankingGauge :percent="percent" class="card-gauge" />
    </div>

    <ol v-if="steps.length" class="step-list">
      <li v-for="(step, index) in steps" :key="step.key" class="step-item">
        <button
          type="button"
          class="step-button"
          :class="{ 'is-highlighted': highlightedIndex === index }"
          :aria-label="
            $t('complete_listing_step_aria', {
              title: $t(step.titleKey),
              percent: step.weightLabel
            })
          "
          @click="handleStepClick(step)"
        >
          <span class="step-number">{{ index + 1 }}</span>
          <span class="step-text">
            <span class="step-title">{{ $t(step.titleKey) }}</span>
            <span class="step-subtitle">{{ $t(step.subtitleKey) }}</span>
          </span>
          <span class="step-pill">{{ step.weightLabel }}</span>
          <svg
            class="step-chevron"
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1.5 1.5 L6 6 L1.5 10.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </li>
    </ol>
  </div>
</template>

<script>
import { fetchCompletionSummary } from '@/utils/listingChecklist';
import AdRankingGauge from './AdRankingGauge.vue';

// Step definitions — the catalog of categories we surface in the list.
// `key` matches the field name on each residence in the API response.
// `seOnly` filters the step out for non-SE countries.
const STEP_DEFS = [
  {
    key: 'images',
    titleKey: 'complete_listing_step_images_title',
    subtitleKey: 'complete_listing_step_images_subtitle',
    section: 'photos'
  },
  {
    key: 'description',
    titleKey: 'complete_listing_step_description_title',
    subtitleKey: 'complete_listing_step_description_subtitle',
    section: 'description'
  },
  {
    key: 'floorplan',
    titleKey: 'complete_listing_step_floorplan_title',
    subtitleKey: 'complete_listing_step_floorplan_subtitle',
    section: 'floorplan'
  },
  {
    key: 'landlord',
    titleKey: 'complete_listing_step_landlord_title',
    subtitleKey: 'complete_listing_step_landlord_subtitle',
    section: 'landlord',
    seOnly: true
  }
];

export default {
  name: 'CompleteListingCard',

  components: { AdRankingGauge },

  props: {
    proposition: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    // Highlight the first incomplete step with the primary-blue
    // background. Used in the modal to nudge the user toward the
    // next action; turned off when the card is shown inline on the
    // Min annons page.
    highlightFirstStep: {
      type: Boolean,
      default: false
    },
    // Completion data the caller already fetched; skips the card's own fetch.
    preloadedSummary: {
      type: Object,
      default: null
    },
    // Limits the checklist to these step keys; null lists every step.
    stepKeys: {
      type: Array,
      default: null
    }
  },

  data() {
    return {
      // Response from /api/my-proposition/completion/{id}. Shape:
      //   { proposition: { current, max },
      //     residences: [{ images: {current,max}, floorplan: {...},
      //                    description: {...}, landlord: {...} }, ...] }
      summary: this.preloadedSummary
    };
  },

  computed: {
    isSE() {
      return this.$country.isCountry('se');
    },

    percent() {
      return this.summary?.proposition?.current ?? 0;
    },

    steps() {
      if (!this.summary) return [];
      const residences = this.summary.residences || [];

      return STEP_DEFS.filter(def => !def.seOnly || this.isSE)
        .filter(def => !this.stepKeys || this.stepKeys.includes(def.key))
        .map(def => {
          // Sum (max - current) across residences for this category.
          // That's how many proposition points the user could still earn
          // by completing this step on whichever residence is missing it.
          const remaining = residences.reduce((sum, r) => {
            const entry = r && r[def.key];
            if (!entry) return sum;
            return sum + Math.max(0, (entry.max ?? 0) - (entry.current ?? 0));
          }, 0);
          return {
            ...def,
            weight: remaining,
            weightLabel: `+${remaining}%`
          };
        })
        .filter(step => step.weight > 0);
    },

    highlightedIndex() {
      // First visible step is the next action — highlight it only
      // when the consumer opted in (typically the modal).
      if (!this.highlightFirstStep) return -1;
      return this.steps.length ? 0 : -1;
    }
  },

  watch: {
    'proposition.propositionId': {
      immediate: true,
      handler(id) {
        if (id && !this.preloadedSummary) this.fetchSummary(id);
      }
    }
  },

  methods: {
    async fetchSummary(propositionId) {
      // null on failure: keep whatever the card already shows.
      const data = await fetchCompletionSummary(propositionId);
      if (data) this.summary = data;
    },

    handleStepClick(step) {
      this.$router.push({
        path: `${this.$routes.EDIT_PROPOSITION}/${this.proposition.propositionId}`,
        query: { section: step.section }
      });

      this.$emit('cta-click', step.key);
    }
  }
};
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #111827;
}

.desc-ring-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.card-description {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
}

.card-gauge {
  width: 68px;
  height: 68px;
  flex-shrink: 0;
}

.step-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.step-button {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }

  &.is-highlighted {
    background: var(--Primary-Color);
    border-color: var(--Primary-Color);
    color: #ffffff;
  }
}

.step-number {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid #bfdbfe;
  font-size: 14px;
  font-weight: 600;
  color: var(--Primary-Color);
  background: transparent;
  line-height: 1;

  .step-button.is-highlighted & {
    border-color: rgba(255, 255, 255, 0.5);
    color: #ffffff;
    background: rgba(255, 255, 255, 0.16);
  }
}

.step-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--Text-Primary);

  .step-button.is-highlighted & {
    color: #ffffff;
  }
}

.step-subtitle {
  font-size: 0.8125rem;
  color: var(--Text-Secondary);
  line-height: 1.3;

  .step-button.is-highlighted & {
    color: rgba(255, 255, 255, 0.85);
  }
}

.step-pill {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--Primary-Color);
  background: #eff6ff;

  .step-button.is-highlighted & {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.2);
  }
}

.step-chevron {
  flex-shrink: 0;
  color: #9ca3af;

  .step-button.is-highlighted & {
    color: rgba(255, 255, 255, 0.9);
  }
}
</style>
